<template>
    <div class="relative">
        <h1 class="font-body-6 text-default-2 xxs:pl-4 lg:pl-0">Groups</h1>
        <div class="xxs:p-4 bg-white rounded border border-white lg:px-6 lg:pt-4 lg:pb-6 mt-4 box-border">
            <div v-if="isAdmin && isMobile" class="fixed z-50 bottom-4 right-4 left-auto min-w-[60px] h-[60px] rounded-full text-white bg-primary-1 flex items-center justify-center py-2 px-4 box-border group transition-all ease-in-out duration-200 cursor-pointer hover:min-w-[120px]">
                <router-link :to="'/groups/create'" class="flex items-center whitespace-nowrap">
                    <span class="text-body-2 block -mr-2 group-hover:mr-0">+</span>
                    <p class="p-0 text-body-6 ml-2 mt-0 mb-0 mr-0 opacity-0 max-w-0 overflow-hidden group-hover:opacity-100 group-hover:max-w-[100px] transition-all ease-in-out duration-200">Add news</p>
                </router-link>
            </div>
            <div class="w-full flex flex-col box-border" v-if="loadingPage">
                <div class="w-full flex xxs:flex-col lg:flex-row mb-5">
                    <div class="skeleton-loader xxs:w-full lg:w-2/5 h-[42px]"></div>
                    <div class="skeleton-loader xxs:w-full md:w-full lg:w-1/4 h-[42px] xxs:mt-4 lg:ml-4 lg:mt-0"></div>
                    <div class="flex-1 flex">
                        <div
                            class="skeleton-loader xxs:w-[49%] lg:w-[20%] h-[42px] xxs:ml-0 xxs:mt-4 lg:mt-0 lg:ml-4 xxs:mr-4"></div>
                        <div class="skeleton-loader flex-1 h-[42px] xxs:mt-4 lg:ml-4 lg:mt-0"></div>
                    </div>
                </div>
                <div class="skeleton-loader w-full mt-1 h-[68px]" v-for="item in perPage - 1" :key="item"></div>
                <div class="flex justify-between w-full mt-6">
                    <div class="w-1/5 skeleton-loader h-[42px]" v-for="i in 3" :key="i"></div>
                </div>
            </div>
            <div class="w-full flex flex-col" v-else>
                <div v-if="hasGroups" class="w-full flex xxs:flex-col lg:flex-row items-end xxs:flex-wrap md:flex-nowrap md:items-center md:justify-between">
                    <CoreInput
                            v-model="search"
                            class="xxs:w-full md:w-full lg:w-2/3 xl:w-[40%]"
                            hasSearchIcon
                            label="Search"
                            placeholder="Enter a keyword to search for a group"
                            @input="handleDebouncedSearch()"
                    >
                        <div class="absolute inset-y-0 right-0 pr-3 flex items-center h-10 cursor-pointer">
                            <img v-if="!search" alt="Search through groups" src="@/assets/icons/search.svg"/>
                            <img v-else src="@/assets/icons/close-light.svg" @click="clearSearchInput" alt="clear"/>
                        </div>
                    </CoreInput>
                    <div class="self-start lg:self-end mt-4 lg:mt-0 xxs:w-full md:w-36">
                        <CoreButton v-if="isAdmin && !isMobile" buttonType="primary" class="xxs:w-full md:w-36 h-12" @click.prevent="router.push({name: 'create-group'})">
                            <span>+</span>
                            <span class="ml-2.5">Add group</span>
                        </CoreButton>
                    </div>
                </div>
                <div class="w-full flex flex-col box-border" v-if="loadingGroups">
                    <div class="skeleton-loader w-full mt-1 h-[67px]" v-for="item in perPage - 1" :key="item"></div>
                </div>
                <div class="flex w-full flex-col mt-5" v-if="!loadingGroups && groups.length > 0">
                    <div class="xxs:hidden lg:flex w-full bg-background-3 text-default-2 text-tag">
                        <div class="w-10 h-8"></div>
                        <div class="lg:flex h-8 flex-1 items-center">
                            Group name
                            <img src="../../assets/icons/sort-asc.svg" alt="" class="w-2 ml-1" />
                        </div>
                        <div class="flex h-8 w-1/5 items-center">
                            Number of members
                        </div>
                        <div class="flex h-8 w-1/4 items-center">
                            Creation date
                            <img src="../../assets/icons/sort-asc.svg" alt="" class="w-2 ml-1" />
                        </div>
                        <div class="w-8 h-8"></div>
                    </div>
                    <div class="flex flex-col" v-if="groups.length > 0">
                        <GroupRow
                            v-for="group in groups"
                            :group="group"
                            :key="group.uid"
                            @deleteGroup="showDeleteGroupModal"
                            @addMember="router.push({name: 'members'})"
                            @membersRemoved="handleSearch"
                        />
                    </div>
                </div>
                <div v-if="emptyState" class="w-full h-[650px] flex justify-center items-center flex-col">
                    <GroupsCreation :color="color.hex" />
                    <p class="mt-8 text-body-4 text-default-1">{{ emptyStateText }}</p>
                    <CoreButton v-if="isAdmin && !isMobile" buttonType="ghost" class="mt-10" @click.prevent="router.push({name: 'create-group'})">
                        <img src="../../assets/icons/plus.svg" alt="" />
                        <span class="ml-2.5">Add group</span>
                    </CoreButton>
                </div>
                <div class="flex w-full flex-col">
                    <Pagination
                        v-if="hasGroups && !emptyState"
                        :from="meta.from ?? 0"
                        :last-page="meta.last_page"
                        :links="links"
                        :to="meta.to ?? 0"
                        :total="meta.total ?? 0"
                        @change="handlePaginationChange"
                    />
                </div>
            </div>
        </div>
    </div>
    <Modal
        :show="deleteGroupModalVisibility"
        @update:show="deleteGroupModalVisibility = false"
        @close="deleteGroupModalVisibility = false"
    >
        <h1 class="text-body-3 mt-3 mb-8 text-center font-bold">Are you sure want to delete {{groupToBeDeleted.name}}?</h1>
        <div class="flex">
            <Button
                buttonType="primary"
                class="w-full mr-4"
                label="Delete"
                @click="handleDeleteGroup"
            />
            <Button
                buttonType="ghost"
                class="w-full"
                label="Cancel"
                @click="deleteGroupModalVisibility = false"
            />
        </div>
    </Modal>
</template>

<script setup>
import { useRouter } from 'vue-router';
import {ref, computed, defineProps, onMounted} from "vue"
import GroupsCreation from "@/components/icons/GroupsCreation.vue";
import {useTheme} from "@/stores/ThemeStore";
import CoreButton from "@/components/core/Button.vue";
const {color} = useTheme();
import {useGroups} from "@/composables/useGroups";
import {useAuth} from "@/stores/AuthStore";
import {storeToRefs} from "pinia";
import CoreInput from "@/components/core/InputGroup.vue";
import GroupRow from "@/components/sections/GroupRow.vue";
import Pagination from "@/components/core/Pagination.vue";
import debounce from 'lodash.debounce'
import Modal from "@/components/core/Modal.vue";
import Button from "@/components/core/Button.vue";
import {toast} from "vue3-toastify";
import {useIsMobile} from "@/composables/useIsMobile";
import {useChat as useChatStore} from "@/stores/ChatStore";

const {groupToBeCreated} = storeToRefs(useChatStore());

const {organization, isAdmin} = storeToRefs(useAuth());
const {getGroups} = useGroups();
const {deleteGroup, deleteChatGroup} = useGroups();
const {isMobile} = useIsMobile();

const router = useRouter();
const props = defineProps(['id']);

const perPage = 15;

const groups = ref([])
const meta = ref(null)
const links = ref([]);
const loadingPage = ref(false);
const loadingGroups = ref(false);
const search = ref('');

const hasGroups = ref(false);

const deleteGroupModalVisibility = ref(false);
const groupToBeDeleted = ref(null);

const emptyState = computed(() => {
    return groups.value.length === 0 && !loadingGroups.value;
});

const emptyStateText = computed(() => {
    return hasGroups.value ? 'We couldn\'t find any groups based on the searched filters' : 'You haven\'t created any groups yet';
});

async function handleSearch(page = 1, perPage = 15) {
    loadingGroups.value = true;

    const request = await getGroups(organization.value.slug, page, perPage, search.value)
    groups.value = request.data;
    meta.value = request.meta;
    links.value = request.links;

    loadingGroups.value = false;
}

const handleDebouncedSearch = debounce(handleSearch, 300);

const clearSearchInput = () => {
    search.value = '';
    handleSearch();
}

const handlePaginationChange = ({page, perPage}) => {
    handleSearch(page, perPage)
}

function showDeleteGroupModal(group) {
    deleteGroupModalVisibility.value = true;
    groupToBeDeleted.value = group;
}

function handleDeleteGroup() {
    deleteGroup(organization.value.slug, groupToBeDeleted.value.uid)
        .then(() => {
            groups.value = groups.value.filter(group => group.uid !== groupToBeDeleted.value.uid);
            deleteGroupModalVisibility.value = false;
            toast.success('Group deleted successfully');
        })
        .catch(() => {
            deleteGroupModalVisibility.value = false;
            toast.error('We couldn\'t delete the group. Please try again later.');
        })
}

onMounted(async () => {
    loadingPage.value = true;

    const response = await getGroups(organization.value.slug, 1, 15);
    groups.value = response.data;
    meta.value = response.meta;
    links.value = response.links;

    hasGroups.value = groups.value.length > 0;
    loadingPage.value = false;
})

</script>
